import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { REHYDRATE } from 'redux-persist'

export interface User {
  id: string
  uid?: string // Firebase UID for compatibility
  name: string
  email: string
  emailVerified?: boolean
  avatar?: string
  roles?: string[] // Add roles property for compatibility
  permissions?: string[]
  level: number
  xp: number
  coins: number
  badges: string[]
  grade: string
  school?: string
  targetScore: number
  createdAt: string
  lastLoginAt: string
  preferences: {
    theme: 'light' | 'dark' | 'system'
    language: string
    notifications: {
      email: boolean
      push: boolean
      sms: boolean
    }
    privacy: {
      profileVisible: boolean
      showProgress: boolean
      showStats: boolean
    }
  }
  subscription: {
    plan: 'free' | 'premium' | 'pro'
    expiresAt?: string
    features: string[]
  }
  stats: {
    totalStudyTime: number
    totalExams: number
    averageScore: number
    currentStreak: number
    longestStreak: number
    totalQuestions: number
    correctAnswers: number
    achievements: number
  }
}

export interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null
  loginAttempts: number
  lastLoginAttempt: string | null
  passwordResetRequested: boolean
  emailVerified: boolean
  twoFactorEnabled: boolean
  rememberMe: boolean
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  loading: false, // Start with false for faster initial load
  error: null,
  loginAttempts: 0,
  lastLoginAttempt: null,
  passwordResetRequested: false,
  emailVerified: false,
  twoFactorEnabled: false,
  rememberMe: false
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Authentication
    loginStart: (state) => {
      state.loading = true
      state.error = null
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; token: string; refreshToken: string }>) => {
      state.loading = false
      state.user = action.payload.user
      state.token = action.payload.token
      state.refreshToken = action.payload.refreshToken
      state.isAuthenticated = true
      state.error = null
      state.loginAttempts = 0
      state.lastLoginAttempt = null
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
      state.loginAttempts += 1
      state.lastLoginAttempt = new Date().toISOString()
    },
    logout: (state) => {
      state.user = null
      state.token = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.loading = false
      state.error = null
      state.loginAttempts = 0
      state.lastLoginAttempt = null
    },

    // Registration
    registerStart: (state) => {
      state.loading = true
      state.error = null
    },
    registerSuccess: (state, action: PayloadAction<{ user: User; token: string; refreshToken: string }>) => {
      state.loading = false
      state.user = action.payload.user
      state.token = action.payload.token
      state.refreshToken = action.payload.refreshToken
      state.isAuthenticated = true
      state.error = null
    },
    registerFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Token Management
    refreshTokenStart: (state) => {
      state.loading = true
    },
    refreshTokenSuccess: (state, action: PayloadAction<{ token: string; refreshToken: string }>) => {
      state.loading = false
      state.token = action.payload.token
      state.refreshToken = action.payload.refreshToken
      state.error = null
    },
    refreshTokenFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
      state.user = null
      state.token = null
      state.refreshToken = null
      state.isAuthenticated = false
    },

    // User Profile
    updateProfileStart: (state) => {
      state.loading = true
      state.error = null
    },
    updateProfileSuccess: (state, action: PayloadAction<Partial<User>>) => {
      state.loading = false
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
      state.error = null
    },
    updateProfileFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Password Management
    changePasswordStart: (state) => {
      state.loading = true
      state.error = null
    },
    changePasswordSuccess: (state) => {
      state.loading = false
      state.error = null
    },
    changePasswordFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Password Reset
    requestPasswordResetStart: (state) => {
      state.loading = true
      state.error = null
      state.passwordResetRequested = false
    },
    requestPasswordResetSuccess: (state) => {
      state.loading = false
      state.passwordResetRequested = true
      state.error = null
    },
    requestPasswordResetFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
      state.passwordResetRequested = false
    },

    // Email Verification
    verifyEmailStart: (state) => {
      state.loading = true
      state.error = null
    },
    verifyEmailSuccess: (state) => {
      state.loading = false
      state.emailVerified = true
      state.error = null
    },
    verifyEmailFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Two Factor Authentication
    enableTwoFactorStart: (state) => {
      state.loading = true
      state.error = null
    },
    enableTwoFactorSuccess: (state) => {
      state.loading = false
      state.twoFactorEnabled = true
      state.error = null
    },
    enableTwoFactorFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    disableTwoFactor: (state) => {
      state.twoFactorEnabled = false
    },

    // Preferences
    updatePreferences: (state, action: PayloadAction<Partial<User['preferences']>>) => {
      if (state.user) {
        state.user.preferences = { ...state.user.preferences, ...action.payload }
      }
    },

    // Stats
    updateStats: (state, action: PayloadAction<Partial<User['stats']>>) => {
      if (state.user) {
        state.user.stats = { ...state.user.stats, ...action.payload }
      }
    },
    addXP: (state, action: PayloadAction<number>) => {
      if (state.user) {
        state.user.xp += action.payload
        // Level up logic
        const newLevel = Math.floor(state.user.xp / 1000) + 1
        if (newLevel > state.user.level) {
          state.user.level = newLevel
          state.user.coins += newLevel * 10 // Bonus coins for leveling up
        }
      }
    },
    addCoins: (state, action: PayloadAction<number>) => {
      if (state.user) {
        state.user.coins += action.payload
      }
    },
    spendCoins: (state, action: PayloadAction<number>) => {
      if (state.user && state.user.coins >= action.payload) {
        state.user.coins -= action.payload
      }
    },
    addBadge: (state, action: PayloadAction<string>) => {
      if (state.user && !state.user.badges.includes(action.payload)) {
        state.user.badges.push(action.payload)
      }
    },

    // Settings
    setRememberMe: (state, action: PayloadAction<boolean>) => {
      state.rememberMe = action.payload
    },

    // Error Management
    clearError: (state) => {
      state.error = null
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },
    clearAuthState: (state) => {
      return initialState
    }
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action) => {
      // Redux Persist rehydration tamamlandığında
      if (action.payload && (action.payload as any).auth) {
        const persistedAuth = (action.payload as any).auth
        // Persisted auth state'i restore et
        return {
          ...state,
          ...persistedAuth,
          loading: false // Rehydration tamamlandığında loading'i false yap
        }
      }
      // Eğer persisted auth data yoksa loading'i false yap
      return {
        ...state,
        loading: false
      }
    })
  }
})

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  registerStart,
  registerSuccess,
  registerFailure,
  refreshTokenStart,
  refreshTokenSuccess,
  refreshTokenFailure,
  updateProfileStart,
  updateProfileSuccess,
  updateProfileFailure,
  changePasswordStart,
  changePasswordSuccess,
  changePasswordFailure,
  requestPasswordResetStart,
  requestPasswordResetSuccess,
  requestPasswordResetFailure,
  verifyEmailStart,
  verifyEmailSuccess,
  verifyEmailFailure,
  enableTwoFactorStart,
  enableTwoFactorSuccess,
  enableTwoFactorFailure,
  disableTwoFactor,
  updatePreferences,
  updateStats,
  addXP,
  addCoins,
  spendCoins,
  addBadge,
  setRememberMe,
  clearError,
  setError,
  setLoading,
  updateUser,
  clearAuthState
} = authSlice.actions

export default authSlice.reducer